import { api } from '@/lib/api'

import { idsFilter } from '@/shared/opensearch/builders/filters'
import {
	createQuery,
	withFilters,
	withSort,
} from '@/shared/opensearch/builders/query'
import { sortDesc } from '@/shared/opensearch/builders/sort'

import { caseListSearchParams } from './lib/case-list-params'
import {
	toInvestigation,
	toInvestigationSummary,
} from './lib/investigation-wire'
import { normalizeMatchedEvents } from './lib/matched-events'

import type { CaseListParams } from './lib/case-list-params'
import type {
	CaseDetails,
	CaseDetailsRequest,
	CaseDetailsResponse,
	CaseDetailsWire,
	CaseEvidenceResponse,
	CaseList,
	CaseListWire,
	Evidence,
	InvestigationHistoryItem,
	InvestigationHistoryResponse,
	MatchedEventsRaw,
} from './types'
import type { SearchLogResponse } from '@/shared/opensearch/types/response'

type GetCaseListOptions = {
	/** The tenant ID; `"all"` for multi-tenant, with `tenants`. */
	tenant: string
	limit?: number
	page?: number
	/** Filters, dates and search, from `toCaseListParams`. */
	params?: CaseListParams
	signal?: AbortSignal
	/** The tenants a multi-tenant request covers (`X-AIR-Request-Tenants`). */
	tenants?: string[]
}

/**
 * Get one page of the cases list: newest first, or most relevant first while
 * searching.
 */
export const getCaseList = async ({
	tenant,
	limit = 5,
	page = 1,
	params = {},
	signal,
	tenants,
}: GetCaseListOptions) => {
	const searchParams = caseListSearchParams(limit, page, params)

	const { data } = await api.get<CaseListWire>(
		`/api/tenants/${tenant}/case?${searchParams.toString()}`,
		{
			signal,
			tenants: tenants,
		},
	)

	// Fold each row's flat investigation block into the one-nullable-`conclusion`
	// view model. The list width carries only verdict + confidence.
	return {
		...data,
		cases: data.cases.map((row) => ({
			...row,
			investigation: toInvestigationSummary(row.investigation),
		})),
	} satisfies CaseList
}

/**
 * Get a case by ID
 * @param tenant - The tenant ID
 * @param id - The case ID
 * @param signal - The signal to abort the request
 */
export const getCase = async (
	tenant: string,
	id: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<CaseDetailsWire>(
		`/api/tenants/${tenant}/case/${id}`,
		{
			signal,
		},
	)

	// Normalize matched_events (v1 flat | v2 wrapped) → the flat shape every
	// consumer + the case_logs cache key speak, and fold the flat investigation
	// block into its view model (one nullable `conclusion`).
	return {
		...data,
		case: {
			...data.case,
			matched_events: normalizeMatchedEvents(
				data.case.matched_events as MatchedEventsRaw,
			),
		},
		investigation: toInvestigation(data.investigation),
	} satisfies CaseDetailsResponse
}

/**
 * Get the curated evidence for a case (latest investigation only).
 * Live API wraps (`{ evidence: [] }`); fixtures are bare arrays — the query
 * `select` unwraps via `data.evidence ?? data`. GET → passes `{ signal }`.
 *
 * @param tenant - The tenant ID
 * @param id - The case (alert) ID
 * @param signal - Optional AbortSignal
 */
export const getCaseEvidence = async (
	tenant: string,
	id: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<CaseEvidenceResponse | Array<Evidence>>(
		`/api/tenants/${tenant}/case/${id}/evidence`,
		{
			signal,
		},
	)

	return data
}

/**
 * Get the full investigation action history (superset of evidence).
 * Live API wraps (`{ investigation_history: [] }`); fixtures are bare arrays.
 * GET → passes `{ signal }`.
 *
 * @param tenant - The tenant ID
 * @param id - The case (alert) ID
 * @param signal - Optional AbortSignal
 */
export const getInvestigationHistory = async (
	tenant: string,
	id: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<
		InvestigationHistoryResponse | Array<InvestigationHistoryItem>
	>(`/api/tenants/${tenant}/case/${id}/investigation_history`, {
		signal,
	})

	return data
}

export const CASE_LOGS_PAGE_SIZE = 100

/**
 * Fetch the raw matched-event logs for a case via OpenSearch.
 *
 * Builds an `ids` query over the event IDs in `matched_events`, paginated by
 * `CASE_LOGS_PAGE_SIZE`. Throws if `matched_events` is empty — there is nothing
 * to look up. Uses POST (search body), so it passes no abort signal (P-03).
 */
export const getCaseLogs = async (
	tenant: string,
	matched_events: CaseDetails['matched_events'],
	page: number = 1,
) => {
	if (!matched_events || Object.keys(matched_events).length === 0) {
		throw new Error('Matched events are required')
	}

	const eventIds = Object.values(matched_events).flat()
	const opensearchQuery = withSort(
		withFilters(
			createQuery({
				size: CASE_LOGS_PAGE_SIZE,
				from: (page - 1) * CASE_LOGS_PAGE_SIZE,
			}),
			[idsFilter(eventIds)],
		),
		[sortDesc('@timestamp')],
	)

	const { data } = await api.post<SearchLogResponse>(
		`/api/tenants/${tenant}/logs/search`,
		{
			query: opensearchQuery,
			sources: [...Object.keys(matched_events).map((key) => key.split(':'))],
		},
	)

	return data
}

/**
 * Change the details of a case
 * @param tenant - The tenant ID
 * @param id - The case ID
 * @param caseDetails - The case details to update
 */
export const changeCaseDetails = async (
	tenant: string,
	id: string,
	caseDetails: CaseDetailsRequest,
) => {
	// Typed as the WIRE envelope, not the view model: nothing folds this response
	// (the mutation invalidates and refetches instead of consuming the body), so
	// claiming a folded `conclusion` here would be a lie.
	const { data } = await api.post<CaseDetailsWire>(
		`/api/tenants/${tenant}/case/${id}`,
		caseDetails,
	)

	return data
}
